<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core';
import { grammarInflection } from '~~/utils';

import TextLink from '~/components/ui/text-link/TextLink.vue';

const props = defineProps<{
	images: string[];
	necessaryImagesCount: number;
	isLoading?: boolean;
	imageAltGenerator?: (index: number) => string;
}>();

const emit = defineEmits<{
	(e: 'image-clicked'): void;
	(e: 'see-all-images'): void;
}>();

const breakpoints = useBreakpoints(breakpointsTailwind);

// SSR-safe: default to desktop (false) on server, update on client
const isMobile = ref(false);

onMounted(() => {
	isMobile.value = breakpoints.isSmaller('md');

	// Watch for changes after mount
	watch(
		() => breakpoints.isSmaller('md'),
		newValue => {
			isMobile.value = newValue;
		}
	);
});

const imageCount = computed(() => props.images.length);
const hasTwoOrMoreImages = computed(() => imageCount.value >= 2);
const hasSevenPlusImages = computed(() => imageCount.value >= 7);

const hasFourOrMoreImages = computed(() => imageCount.value >= 4);

const shouldShowGrid = computed(() =>
	isMobile.value ? hasTwoOrMoreImages.value : hasFourOrMoreImages.value
);

const firstRowImages = computed(() => {
	if (isMobile.value && hasTwoOrMoreImages.value) {
		return [props.images[1]];
	}

	if (!isMobile.value && hasFourOrMoreImages.value) {
		return props.images.slice(2, 4);
	}

	return [];
});

const secondRowImages = computed(() => {
	if (isMobile.value && hasFourOrMoreImages.value) {
		return props.images.slice(2, 4);
	}

	if (!isMobile.value && hasSevenPlusImages.value) {
		return props.images.slice(4, 7);
	}

	return [];
});

const firstRowOffset = computed(() => {
	if (hasTwoOrMoreImages.value && isMobile.value) {
		return 2;
	}

	if (hasFourOrMoreImages.value && !isMobile.value) {
		return 3;
	}

	return 0;
});

const secondRowOffset = computed(() => {
	if (hasFourOrMoreImages.value && isMobile.value) {
		return 3;
	}

	if (hasSevenPlusImages.value && !isMobile.value) {
		return 5;
	}

	return 0;
});

const ctaText = computed(
	() =>
		`${imageCount.value} ${grammarInflection(imageCount.value, 'foto', 'fotos')}`
);

const getImageAlt = (index: number) => {
	return props.imageAltGenerator?.(index) ?? `Imagem ${index + 1}`;
};

function handleImageClick() {
	emit('image-clicked');
}

function handleSeeAllImages() {
	emit('see-all-images');
}
</script>

<template>
	<div v-if="isLoading">
		<div class="grid grid-cols-6 gap-3 pb-5">
			<Skeleton
				:is-active="true"
				class="col-span-3 h-[310px]"
				variant="white"
			/>
			<Skeleton
				:is-active="true"
				class="col-span-3 h-[310px]"
				variant="white"
			/>
			<Skeleton
				:is-active="true"
				class="col-span-2 h-[220px]"
				variant="white"
			/>
			<Skeleton
				:is-active="true"
				class="col-span-2 h-[220px]"
				variant="white"
			/>
			<Skeleton
				:is-active="true"
				class="col-span-2 h-[220px]"
				variant="white"
			/>
		</div>

		<Skeleton :is-active="true" class="h-5 w-[120px]" variant="white" />
	</div>

	<div
		v-else-if="shouldShowGrid"
		class="relative flex w-full flex-col gap-3 overflow-hidden"
	>
		<div class="relative grid gap-3 overflow-hidden">
			<div>
				<div class="flex flex-col gap-3">
					<div class="order-1 grid w-full grid-cols-1 gap-3 md:grid-cols-2">
						<NuxtImg
							v-for="(image, index) in firstRowImages"
							:key="`first-row-${image}-${index}`"
							:src="image"
							width="800"
							sizes="xs:100vw sm:100vw md:50vw lg:50vw xl:50vw 2xl:50vw"
							densities="x1"
							format="webp"
							:alt="getImageAlt(index + firstRowOffset)"
							class="aspect-[4/3] max-h-[240px] w-full cursor-pointer object-cover md:max-h-[306px]"
							decoding="async"
							loading="lazy"
							@click="handleImageClick"
						/>
					</div>

					<div
						v-if="secondRowImages.length > 0"
						class="order-2 grid grid-cols-2 gap-3 md:grid-cols-3"
					>
						<NuxtImg
							v-for="(item, index) in secondRowImages"
							:key="`second-row-${item}-${index}`"
							:src="item"
							width="640"
							sizes="xs:50vw sm:50vw md:33vw lg:33vw xl:33vw 2xl:33vw"
							densities="x1"
							format="webp"
							:alt="getImageAlt(index + secondRowOffset)"
							class="aspect-[4/3] max-h-[148px] w-full cursor-pointer object-cover md:max-h-[216px]"
							decoding="async"
							loading="lazy"
							@click="handleImageClick"
						/>
					</div>
				</div>
			</div>
		</div>

		<button class="mt-4 flex items-center gap-2" @click="handleSeeAllImages">
			<IconMaterialSymbolsLightImageOutline class="size-5" />
			<TextLink>
				{{ ctaText }}
			</TextLink>
		</button>
	</div>
</template>
